import axios from "axios";
import { logout } from "utils/accounts";
import Cookies from "js-cookie";
import Bugsnag from "@bugsnag/js";

let interceptorsRegistered = false;
let csrfBootstrapPromise = null;
const reportedCsrfFailures = new WeakSet();

const isSameOriginRequest = (config) => {
  try {
    const base = new URL(config.baseURL || "/", window.location.origin);
    return new URL(config.url, base).origin === window.location.origin;
  } catch {
    return false;
  }
};

const safeRequestPath = (url) => {
  if (typeof url !== "string" || !url) return "unknown";
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return "unknown";
  }
};

const responseDiagnostics = (response) => {
  const header = (name) => {
    const value = response?.headers?.get
      ? response.headers.get(name)
      : response?.headers?.[name];
    return typeof value === "string" ? value.slice(0, 200) : undefined;
  };
  return {
    status: response?.status,
    diagnosticId: header("x-csrf-diagnostic-id"),
    cacheControl: header("cache-control"),
    cacheStatus: header("cf-cache-status"),
    age: header("age"),
    responseDate: header("date"),
    contentType: header("content-type"),
  };
};

const cookieCount = (name) =>
  document.cookie
    .split(";")
    .filter((part) => part.trim().startsWith(`${name}=`)).length;

const leaveCsrfBreadcrumb = (message, metadata = {}) => {
  console.warn(`[API] ${message}`, metadata);

  if (typeof Bugsnag?.leaveBreadcrumb === "function") {
    Bugsnag.leaveBreadcrumb(message, metadata, "request");
  }
};

const notifyCsrfFailure = (message, metadata = {}, cause) => {
  // Nested Axios retries unwind through each interceptor. Report the actual
  // failure once, with the stage at which it occurred.
  if (cause && typeof cause === "object") {
    if (reportedCsrfFailures.has(cause)) return;
    reportedCsrfFailures.add(cause);
  }
  leaveCsrfBreadcrumb(message, metadata);
  // Console warnings are not automatically forwarded by our Logs SDK setup.
  void import("@openobserve/browser-logs")
    .then(({ openobserveLogs }) => {
      if (openobserveLogs.getInitConfiguration()) {
        openobserveLogs.logger.warn(message, { csrf: metadata });
      }
    })
    .catch(() => {});

  if (typeof Bugsnag?.notify === "function") {
    Bugsnag.notify(new Error(message), (event) => {
      event.addMetadata("csrf", metadata);
    });
  }
};

export const getCSRFToken = async (
  force = false,
  onFetched = (_metadata) => {}
) => {
  const CSRFToken = Cookies.get("XSRF-TOKEN");

  if (CSRFToken && !force) return CSRFToken;

  try {
    const res = await axios("/csrf-token");

    if (res.data && res.data.token) {
      // NOTE: (Andrey) add CSRF token to cookie without domain to avoid add dot before domain
      Cookies.set("XSRF-TOKEN", res.data.token, {
        path: "/",
      });
    }
    const diagnostics = {
      ...responseDiagnostics(res),
      tokenPresent: Boolean(res.data?.token),
      cookieMatchesToken:
        Boolean(res.data?.token) &&
        Cookies.get("XSRF-TOKEN") === res.data.token,
      sessionCookieCount: cookieCount("sked.session_id"),
      csrfCookieCount: cookieCount("XSRF-TOKEN"),
      serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
    };
    onFetched(diagnostics);
    if (force) leaveCsrfBreadcrumb("CSRF token fetched", diagnostics);
    return res.data?.token || undefined;
  } catch (error) {
    console.log("Get CSRF Token Error", {
      ...responseDiagnostics(error?.response),
      code: error?.code,
    });
    throw error;
  }
};

export const initAPI = async () => {
  const CSRF_TOKEN_MAX_RETRY = 3;

  // Remove deprecated global CSRF token and tokens with dot, for now we use separated token for each hostname without dot (domain)
  Cookies.remove("XSRF-TOKEN", { domain: ".skedsocial.com" });
  Cookies.remove("XSRF-TOKEN", { domain: ".app.skedsocial.com" });
  Cookies.remove("XSRF-TOKEN", { domain: ".staging.skedsocial.com" });
  Cookies.remove("XSRF-TOKEN", { domain: ".staging-n18.skedsocial.com" });

  if (!interceptorsRegistered) {
    interceptorsRegistered = true;

    axios.interceptors.response.use(null, async function (error) {
      // NOTE: (Andrey) catch bad CSRF token errors
      if (
        error?.response?.status === 403 &&
        error?.response?.data?.csrfReset === 1
      ) {
        const requestUrl = safeRequestPath(error?.config?.url);
        const csrfTokenRetry = error?.config?._csrfTokenRetry || 0;

        // NOTE: (Andrey) If token is wrong try get new one only 3 times (avoid infinity getting token)
        if (csrfTokenRetry >= CSRF_TOKEN_MAX_RETRY) {
          notifyCsrfFailure(
            "CSRF retry limit reached",
            {
              requestUrl,
              stage: "request_replay",
              maxRetries: CSRF_TOKEN_MAX_RETRY,
              ...responseDiagnostics(error.response),
              refresh: error.config?._csrfRefreshDiagnostics,
            },
            error
          );

          throw error;
        }

        const attempt = csrfTokenRetry + 1;
        leaveCsrfBreadcrumb("Retrying request after CSRF reset", {
          attempt,
          requestUrl,
          rejection: responseDiagnostics(error.response),
        });

        let stage = "token_fetch";
        let refresh = {};
        try {
          // NOTE: (Andrey) Try force get new token
          const csrfToken = await getCSRFToken(true, (metadata) => {
            refresh = metadata;
          });

          if (!csrfToken) {
            throw new Error("CSRF refresh did not return a token");
          }

          const retryConfig = {
            ...error.config,
            _csrfTokenRetry: attempt,
            _csrfRefreshDiagnostics: refresh,
          };

          if (retryConfig.headers?.set) {
            retryConfig.headers.set("X-XSRF-TOKEN", csrfToken);
          } else {
            retryConfig.headers = {
              ...retryConfig.headers,
              "X-XSRF-TOKEN": csrfToken,
            };
          }

          // Echo only our opaque ID, never the token, to join issuance/rejection logs.
          if (
            isSameOriginRequest(retryConfig) &&
            /^[a-f0-9-]{36}$/i.test(refresh.diagnosticId || "")
          ) {
            if (retryConfig.headers?.set) {
              retryConfig.headers.set(
                "X-CSRF-Refresh-Id",
                refresh.diagnosticId
              );
            } else {
              retryConfig.headers["X-CSRF-Refresh-Id"] = refresh.diagnosticId;
            }
          }

          // NOTE: (Andrey) retry request with new token
          stage = "request_replay";
          const response = await axios(retryConfig);
          leaveCsrfBreadcrumb("CSRF retry succeeded", {
            attempt,
            requestUrl,
          });
          return response;
        } catch (retryError) {
          notifyCsrfFailure(
            "CSRF retry failed",
            {
              attempt,
              requestUrl,
              stage,
              refresh,
              ...responseDiagnostics(retryError?.response),
              failurePath: safeRequestPath(retryError?.config?.url),
              errorCode: retryError?.code,
              csrfReset: retryError?.response?.data?.csrfReset === 1,
            },
            retryError
          );
          throw retryError;
        }
      }

      // NOTE: (Andrey) catch unauthorized and logout, go to login page
      // Skip logout for /api-jwt — that 401 is handled by the token refresh
      // retry logic in _tokenRefresh.js and should degrade gracefully.
      if (error?.response?.status === 401) {
        const requestUrl = error?.config?.url || "";
        const isBootstrapRedirect =
          requestUrl === "/user/bootstrap" &&
          error?.response?.data?.type === "bootstrap_redirect";
        const isResourceTokenRequest = requestUrl.includes(
          "/auth/sked-auth/resource-token"
        );
        if (
          !requestUrl.includes("/api-jwt") &&
          !isResourceTokenRequest &&
          !isBootstrapRedirect
        ) {
          logout();
        }
      }

      if (error?.response?.status >= 500 && Bugsnag) {
        Bugsnag.notify(error);
      }

      throw error;
    });
  }

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = getCSRFToken().catch((error) => {
      csrfBootstrapPromise = null;
      notifyCsrfFailure("Initial CSRF bootstrap failed", {
        errorMessage: error?.message,
      });
      throw error;
    });
  }

  return csrfBootstrapPromise;
};
